import { format } from "date-fns";
import i18n from "../../i18n";
import { CapacityMonth, capacityMonthLabel, capacityRows } from "../reports/capacitySeries";
import * as XLSX from 'xlsx';
import { translateAnimalCategoryDescription } from '../../i18n/translateAnimalCategory';
import { applyColumnWidths, applyColumnWidthsFromArrays } from './columnWidthCalculator';

interface IBalance {
  id: number;
  opening_balance: number;
  end_balance: number;
  age_description: string;
  description: string;
  name: string;
  birth: number;
  internal_in_transfer: number;
  internal_out_transfer: number;
  shopping: number;
  sales: number;
  external_in_transfer: number;
  external_out_transfer: number;
  death: number;
  evolution: number;
  inventory_adjustment: number;
}

interface IBalanceAnimal {
  category_id: number;
  age_description: string;
  name: string;
  type: string;
  quantity: number;
  average_weight: number;
  total_weight: number;
  total_arroba: number;
  total_uas: number;
}

export const exportCattleMapDataToExcel = (
  balance: IBalance[],
  balanceAnimal: IBalanceAnimal[],
  capacityMonths: CapacityMonth[],
  startDate?: Date,
  endDate?: Date
) => {
  if (!balance || !balanceAnimal || !capacityMonths) {
    throw new Error(i18n.t("reports.capacity.insufficientData"));
  }

  const workbook = XLSX.utils.book_new();

  const balanceData = [...balance].sort((a, b) => a.id - b.id).map((item) => ({
    [i18n.t("reports.animalCategory")]: translateAnimalCategoryDescription(item),
    [i18n.t("reports.openingBalance")]: item.opening_balance,
    [i18n.t("reports.births")]: item.birth,
    [i18n.t("reports.capacity.internalEntry")]: item.internal_in_transfer,
    [i18n.t("reports.capacity.externalEntry")]: item.external_in_transfer,
    [i18n.t("reports.purchases")]: item.shopping,
    [i18n.t("reports.sales")]: item.sales,
    [i18n.t("reports.capacity.internalExit")]: item.internal_out_transfer,
    [i18n.t("reports.capacity.externalExit")]: item.external_out_transfer,
    [i18n.t("reports.deaths")]: item.death,
    [i18n.t("reports.evolution")]: item.evolution,
    [i18n.t("reports.periodAdjustment")]: item.inventory_adjustment,
    [i18n.t("reports.balanceLabel")]: item.end_balance
  }));

  const totalRow = {
    [i18n.t("reports.animalCategory")]: i18n.t("reports.total"),
    [i18n.t("reports.openingBalance")]: balance.reduce((acc, item) => acc + item.opening_balance, 0),
    [i18n.t("reports.births")]: balance.reduce((acc, item) => acc + item.birth, 0),
    [i18n.t("reports.capacity.internalEntry")]: balance.reduce((acc, item) => acc + item.internal_in_transfer, 0),
    [i18n.t("reports.capacity.externalEntry")]: balance.reduce((acc, item) => acc + item.external_in_transfer, 0),
    [i18n.t("reports.purchases")]: balance.reduce((acc, item) => acc + item.shopping, 0),
    [i18n.t("reports.sales")]: balance.reduce((acc, item) => acc + item.sales, 0),
    [i18n.t("reports.capacity.internalExit")]: balance.reduce((acc, item) => acc + item.internal_out_transfer, 0),
    [i18n.t("reports.capacity.externalExit")]: balance.reduce((acc, item) => acc + item.external_out_transfer, 0),
    [i18n.t("reports.deaths")]: balance.reduce((acc, item) => acc + item.death, 0),
    [i18n.t("reports.evolution")]: balance.reduce((acc, item) => acc + item.evolution, 0),
    [i18n.t("reports.periodAdjustment")]: balance.reduce((acc, item) => acc + item.inventory_adjustment, 0),
    [i18n.t("reports.balanceLabel")]: balance.reduce((acc, item) => acc + item.end_balance, 0)
  };

  const balanceWorksheet = XLSX.utils.json_to_sheet([...balanceData, totalRow]);
  applyColumnWidths(balanceWorksheet, [...balanceData, totalRow]);
  XLSX.utils.book_append_sheet(workbook, balanceWorksheet, i18n.t("reports.balance"));

  const processedAnimals = balanceAnimal
    .reduce((acc: IBalanceAnimal[], item) => {
      const existingCategory = acc.find(cat => cat.category_id === item.category_id);
      if (existingCategory) {
        existingCategory.quantity += item.quantity;
        existingCategory.total_weight += item.total_weight;
        existingCategory.total_arroba += item.total_arroba;
        existingCategory.total_uas += item.total_uas;
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, [])
    .sort((a, b) => a.category_id - b.category_id);

  const totalUAs = processedAnimals.reduce((acc, item) => acc + item.total_uas, 0);

  const animalData = processedAnimals.map((item) => ({
    [i18n.t("reports.animalCategory")]: translateAnimalCategoryDescription(item),
    [i18n.t("reports.finalStock")]: item.quantity,
    [i18n.t("reports.averageWeightKg")]: parseFloat(item.average_weight.toFixed(0)),
    [i18n.t("reports.totalWeightKg")]: item.total_weight,
    [i18n.t("reports.totalArrobas")]: parseFloat(item.total_arroba.toFixed(2)),
    [i18n.t("reports.totalUa")]: parseFloat(item.total_uas.toFixed(2)),
    [i18n.t("reports.uaDistribution")]: new Intl.NumberFormat(i18n.language, { style: "percent", maximumFractionDigits: 2 }).format(totalUAs ? item.total_uas / totalUAs : 0)
  }));

  const animalTotalRow = {
    [i18n.t("reports.animalCategory")]: i18n.t("reports.total"),
    [i18n.t("reports.finalStock")]: processedAnimals.reduce((acc, item) => acc + item.quantity, 0),
    [i18n.t("reports.averageWeightKg")]: '',
    [i18n.t("reports.totalWeightKg")]: processedAnimals.reduce((acc, item) => acc + item.total_weight, 0),
    [i18n.t("reports.totalArrobas")]: parseFloat(processedAnimals.reduce((acc, item) => acc + item.total_arroba, 0).toFixed(2)),
    [i18n.t("reports.totalUa")]: parseFloat(processedAnimals.reduce((acc, item) => acc + item.total_uas, 0).toFixed(2)),
    [i18n.t("reports.uaDistribution")]: ''
  };

  const animalWorksheet = XLSX.utils.json_to_sheet([...animalData, animalTotalRow]);
  applyColumnWidths(animalWorksheet, [...animalData, animalTotalRow]);
  XLSX.utils.book_append_sheet(workbook, animalWorksheet, i18n.t("reports.animalCategory"));

  const returnedMonths = capacityMonths.filter(month => month.data !== undefined);
  const rows = capacityRows(returnedMonths, true).slice(0, 2);
  const capacityData = [
    [i18n.t("reports.stockingMonth"), ...returnedMonths.map(capacityMonthLabel)],
    ...rows,
  ];

  const capacityWorksheet = XLSX.utils.aoa_to_sheet(capacityData);
  applyColumnWidthsFromArrays(capacityWorksheet, capacityData);
  XLSX.utils.book_append_sheet(workbook, capacityWorksheet, i18n.t("reports.capacity.sheet"));

  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array' 
  });

  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  const prefix = i18n.t("reports.capacity.filename");
  const filename = startDate && endDate
    ? `${prefix}-${format(startDate, "yyyy-MM-dd")}-${format(endDate, "yyyy-MM-dd")}.xlsx`
    : `${prefix}-${format(new Date(), "yyyy-MM-dd")}.xlsx`;

  link.download = filename;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
